import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDownRight, Clock, ShieldCheck, RefreshCw } from "lucide-react";
import { Sigil } from "@/components/Sigil";
import { scrollToId } from "@/lib/api";

const LINES = [
  { t: "Graphics", cls: "text-[var(--ink)]" },
  { t: "fit for", cls: "italic font-medium text-[var(--ink-dim)]" },
  { t: "a King.", cls: "text-gilded" },
];

const MaskLine = ({ children, i, cls }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${cls}`}
      initial={{ y: "110%", rotate: 3 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.2, delay: 0.25 + i * 0.14, ease: [0.76, 0, 0.24, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const HeroArt = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div className="relative" style={{ perspective: 1200 }} onMouseMove={move} onMouseLeave={reset} data-testid="hero-art">
      <Sigil className="absolute -inset-[18%] w-[136%] h-[136%] text-[var(--arcane)]/40 spin-slow" />
      <Sigil className="absolute -inset-[6%] w-[112%] h-[112%] text-[var(--gold)]/25 spin-rev" />
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, scale: 1.08, filter: "blur(12px)" }}
        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
        transition={{ duration: 1.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative"
      >
        <div className="arch-clip relative aspect-[3/4] overflow-hidden border border-[var(--gold)]/40 shadow-[0_40px_120px_-20px_rgba(224,38,58,0.55)]">
          <img src="/art/vamp-throne.jpg" alt="Gothic throne artwork by Vamp Graphics" className="w-full h-full object-cover object-top scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--void)] via-transparent to-transparent" />
        </div>
        <div style={{ transform: "translateZ(60px)" }}
          className="absolute -left-4 sm:-left-10 bottom-10 bg-[var(--crypt)]/90 backdrop-blur-md border border-[var(--gold)]/40 px-4 py-3 hex-clip">
          <div className="font-mono text-[10px] tracking-[0.3em] text-[var(--gold)]">FROM</div>
          <div className="font-display text-3xl font-bold">$20</div>
        </div>
        <div style={{ transform: "translateZ(80px)" }}
          className="absolute -right-3 sm:-right-8 top-[22%] bg-[var(--blood)] px-3 py-2 font-mono text-[11px] tracking-[0.2em] uppercase shadow-lg">
          Free redo
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yArt = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen pt-28 pb-16 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[10%] right-[8%] w-[520px] h-[520px] rounded-full bg-[var(--blood)]/20 blur-[140px]" />
        <div className="absolute bottom-0 left-[-10%] w-[480px] h-[480px] rounded-full bg-[var(--arcane)]/15 blur-[140px]" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }}
            className="font-mono text-xs tracking-[0.35em] uppercase text-[var(--gold)] flex items-center gap-3">
            <span className="w-2 h-2 bg-[var(--blood)] rotate-45 animate-pulse" /> Graphic Designer · Est. by Vamp
          </motion.div>
          <h1 data-testid="hero-title" className="font-display font-black text-[15vw] sm:text-7xl lg:text-[7.5rem] leading-[0.92] tracking-tight mt-6">
            {LINES.map((l, i) => <MaskLine key={l.t} i={i} cls={l.cls}>{l.t}</MaskLine>)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-[var(--ink-dim)] leading-relaxed">
            Graphic art, image-to-video art, videos, profile pictures, cover art, advertisements and websites — crafted
            professionally to take your business to the next level.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9 }}
            className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-commission-btn" onClick={() => scrollToId("contact")}
              className="btn-blood hex-clip px-8 py-4 font-mono text-sm tracking-[0.2em] uppercase flex items-center gap-3">
              Commission work <ArrowDownRight className="w-4 h-4" />
            </button>
            <button data-testid="hero-pricing-btn" onClick={() => scrollToId("pricing")}
              className="btn-ghost px-8 py-4 font-mono text-sm tracking-[0.2em] uppercase">
              See pricing
            </button>
          </motion.div>
          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 1 }}
            className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
            {[[Clock, "Reply in 1–2 business days"], [ShieldCheck, "Professional work"], [RefreshCw, "Free image redo"]].map(([I, t]) => (
              <li key={t} className="flex items-center gap-3 text-sm text-[var(--ink-dim)]">
                <I className="w-4 h-4 text-[var(--gold)] shrink-0" /> {t}
              </li>
            ))}
          </motion.ul>
        </motion.div>
        <motion.div style={{ y: yArt }} className="lg:col-span-5 w-full max-w-[460px] mx-auto">
          <HeroArt />
        </motion.div>
      </div>
    </section>
  );
};
